import { ref, onMounted, onUnmounted, provide, inject, type Ref, nextTick } from 'vue';

// Define the interface for the provided/injected value
interface PrintMediaContext {
  isPrinting: Ref<boolean>;
  printWindow: () => void;
}

// Unique symbol for provide/inject
const PrintMediaSymbol = Symbol('printMedia');

export function providePrintMedia() {
  const isPrinting = ref(false);

  // Handle matchMedia for print preview (Ctrl+P)
  const printMediaQuery = window.matchMedia('print');
  const updatePrintState = () => {
    isPrinting.value = printMediaQuery.matches;
  };

  // Handle beforeprint/afterprint for other print scenarios
  const handleBeforePrint = () => {
    isPrinting.value = true;
  };

  const handleAfterPrint = () => {
    isPrinting.value = false;
  };

  // Global print function
  const printWindow = async () => {
    isPrinting.value = true;
    // Wait for the next DOM update cycle to ensure colspan is updated
    await nextTick();
    window.print();
  };

  onMounted(() => {
    // Set up matchMedia listener
    printMediaQuery.addEventListener('change', updatePrintState);
    updatePrintState();

    // Set up print event listeners
    window.addEventListener('beforeprint', handleBeforePrint);
    window.addEventListener('afterprint', handleAfterPrint);
  });

  onUnmounted(() => {
    // Clean up matchMedia listener
    printMediaQuery.removeEventListener('change', updatePrintState);

    // Clean up print event listeners
    window.removeEventListener('beforeprint', handleBeforePrint);
    window.removeEventListener('afterprint', handleAfterPrint);
  });

  // Provide the context with isPrinting and triggerPrint
  provide<PrintMediaContext>(PrintMediaSymbol, { isPrinting, printWindow });
}

export function usePrinting() {
  const printMedia = inject<PrintMediaContext>(PrintMediaSymbol);
  if (!printMedia) {
    throw new Error('usePrintMedia must be used within a component where providePrintMedia is called.');
  }
  return printMedia;
}